import React, { type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Logo } from '../Logo';

/**
 * Shared Snakker deck primitives. Every slide is a locked 16:9 frame that fills
 * the stage edge to edge, sized with fluid clamp() values so the same layout
 * scales down on small viewports without reflowing.
 */
export const t = {
  hero: 'text-[clamp(32px,6.1vw,120px)]',
  title: 'text-[clamp(24px,3.65vw,72px)]',
  lead: 'text-[clamp(14px,2vw,39px)]',
  body: 'text-[clamp(11px,1.52vw,30px)]',
  small: 'text-[clamp(10.5px,1.38vw,27px)]',
  label: 'text-[clamp(8.5px,1.02vw,20px)]',
  stat: 'text-[clamp(26px,4vw,78px)]'
};

/** Shared slide padding — tight margins so content uses the full frame. */
export const PAD = 'px-[4vw] pt-[4.4vh] pb-[3.8vh]';
type RevealProps = {
  children: ReactNode;
  delay?: number;
  className?: string;
};
export function Reveal({
  children,
  delay = 0,
  className
}: RevealProps) {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) return <div className={className} data-id="element-207" data-mp="1rvd03pmkqdvk">{children}</div>;
  return <motion.div className={className} initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.55,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-208" data-mp="1rttk804ty4ub">
      {children}
    </motion.div>;
}
type SlideFrameProps = {
  children: ReactNode;
  className?: string;
};
export function SlideFrame({
  children,
  className
}: SlideFrameProps) {
  return <section className={`relative flex h-full w-full flex-col overflow-hidden bg-[#0C0C0C] text-white ${className ?? ''}`} data-id="element-209" data-mp="1943pte4zbl2x">
      {children}
    </section>;
}
type LogoChipProps = {
  className?: string;
};

/**
 * The brand mark sits on a light chip so it stays legible on the deck's
 * near-black surfaces regardless of the asset's own color.
 */
export function LogoChip({
  className
}: LogoChipProps) {
  return <span className={`inline-flex items-center justify-center rounded-[0.35vw] bg-white px-[0.7vw] py-[0.7vh] ${className ?? ''}`} data-id="element-210" data-mp="u84kgb997z1h">
      <Logo className="h-[clamp(9px,1.25vw,25px)] w-auto" data-id="element-211" data-mp="2wvl4f90dg23x" />
    </span>;
}
type SlideHeaderProps = {
  eyebrow: string;
  title: string;
  /** Trailing phrase of the headline, set in the accent colour. */
  accent?: string;
  kicker?: string;
};
export function SlideHeader({
  eyebrow,
  title,
  accent,
  kicker
}: SlideHeaderProps) {
  return <header className="shrink-0" data-id="element-212" data-mp="2i42dig5z4n5s">
      <Reveal className="flex items-center justify-between" data-id="element-213" data-mp="3fu71mcsrp92k">
        <div className="flex items-center gap-[1vw]" data-id="element-214" data-mp="3frb7kzk1j4je">
          <LogoChip data-id="element-215" data-mp="3avm0mb5oyld2" />
          <span className="flex items-center gap-[0.6vw]" data-id="element-216" data-mp="3avdpoot4pq4i">
            <span aria-hidden className="h-[clamp(6px,0.7vw,14px)] w-[clamp(6px,0.7vw,14px)] bg-[#7D38E5]" data-id="element-217" data-mp="35uq1sc075pei" />
            <span className={`${t.label} font-semibold uppercase tracking-[0.22em] text-[#A26BFF]`} data-id="element-218" data-mp="31vz1jw0fprn6">
              {eyebrow}
            </span>
          </span>
        </div>
        {kicker ? <span className={`${t.label} font-medium uppercase tracking-[0.22em] text-white/35`} data-id="element-219" data-mp="5wotlhrsw4w7">
            {kicker}
          </span> : null}
      </Reveal>
      <Reveal delay={0.08} data-id="element-220" data-mp="9vxk8drjnmnj">
        <h2 className={`${t.title} mt-[1.8vh] max-w-[88%] font-semibold leading-[1.04] tracking-[-0.02em]`} data-id="element-221" data-mp="ewv2eqck0gp1">
          {title}
          {accent ? <span className="text-[#A26BFF]" data-id="element-222" data-mp="jq6asvx0l5j2"> {accent}</span> : null}
        </h2>
      </Reveal>
      <Reveal delay={0.14} data-id="element-223" data-mp="omwr9kcqe350">
        <div className="mt-[1.5vh] h-[3px] w-[6vw] bg-[#7D38E5]" data-id="element-224" data-mp="tm99tswkko1s" />
      </Reveal>
    </header>;
}
type PanelProps = {
  label: string;
  children: ReactNode;
  index?: string;
  className?: string;
};
export function Panel({
  label,
  children,
  index,
  className
}: PanelProps) {
  return <div className={`flex min-h-0 flex-col rounded-[0.8vw] border border-white/10 bg-white/[0.035] p-[1.35vw] ${className ?? ''}`} data-id="element-225" data-mp="12klbqr2vz89j">
      <div className="flex items-center gap-[0.7vw]" data-id="element-226" data-mp="34dizi8ht3anm">
        {index ? <span className={`${t.label} flex h-[clamp(14px,1.7vw,34px)] min-w-[clamp(14px,1.7vw,34px)] items-center justify-center bg-[#7D38E5] px-[0.3vw] font-semibold text-white`} data-id="element-227" data-mp="293vv17ypfgtf">
            {index}
          </span> : null}
        <h3 className={`${t.label} font-semibold uppercase tracking-[0.18em] text-white/70`} data-id="element-228" data-mp="2j1k2myhesit0">
          {label}
        </h3>
      </div>
      <div className="mt-[1.6vh] flex min-h-0 flex-col gap-[1.3vh]" data-id="element-229" data-mp="1qd4n8aud7yh5">
        {children}
      </div>
    </div>;
}
type BulletProps = {
  children: ReactNode;
};
export function Bullet({
  children
}: BulletProps) {
  return <p className={`${t.body} flex gap-[0.7vw] leading-[1.4] text-white/85`} data-id="element-230" data-mp="20493ddvs1pdk">
      <span aria-hidden className="mt-[0.75em] h-[2px] w-[1vw] shrink-0 bg-[#7D38E5]" data-id="element-231" data-mp="1vchauxt299ks" />
      <span data-id="element-232" data-mp="17mmzkkoe3t2n">{children}</span>
    </p>;
}
type RowProps = {
  title: string;
  description: string;
  value?: string;
};
export function Row({
  title,
  description,
  value
}: RowProps) {
  return <div className="flex items-baseline justify-between gap-[1.2vw] border-b border-white/[0.08] pb-[1.2vh] last:border-b-0 last:pb-0" data-id="element-233" data-mp="3ebozxdr92sk1">
      <div className="min-w-0" data-id="element-234" data-mp="3j2x8y8oo4bwo">
        <p className={`${t.small} font-semibold leading-[1.25] text-white`} data-id="element-235" data-mp="3j48as2yjz9l5">
          {title}
        </p>
        <p className={`${t.body} mt-[0.5vh] leading-[1.35] text-white/60`} data-id="element-236" data-mp="34bempt57tl4v">
          {description}
        </p>
      </div>
      {value ? <p className={`${t.small} shrink-0 whitespace-nowrap font-semibold text-[#A26BFF]`} data-id="element-237" data-mp="w932glsnkpy">
          {value}
        </p> : null}
    </div>;
}
type StatProps = {
  value: string;
  label: string;
};
export function Stat({
  value,
  label
}: StatProps) {
  return <div data-id="element-238" data-mp="3s2aif1csl0he">
      <p className={`${t.stat} font-semibold leading-none tracking-[-0.03em]`} data-id="element-239" data-mp="3s1sgbo3g7s1k">
        {value}
      </p>
      <p className={`${t.label} mt-[1.2vh] uppercase tracking-[0.16em] text-white/45`} data-id="element-240" data-mp="2cl0h0k7cvf4a">
        {label}
      </p>
    </div>;
}